import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { PortalProfileService } from '../services/portal/portalProfileService';
import { PortalMessagesService } from '../services/portal/portalMessagesService';
import { PortalAgreementsService } from '../services/portal/portalAgreementsService';

const ClientPortalContext = createContext(null);

export const ClientPortalProvider = ({ children }) => {
  const { user, role } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ messages: 0, agreements: 0 });

  const loadClient = useCallback(async () => {
    if (!user?.id) {
      setClient(null);
      setError(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    const { data, error } = await PortalProfileService.getClientProfile({
      authUserId: user.id,
      email: user.email
    });
    if (error) {
      setError(error);
      setClient(null);
    } else {
      setClient(data);
      setError(null);
    }
    setLoading(false);
    return data;
  }, [user?.id, user?.email]);

  const loadCounts = useCallback(async (clientId) => {
    if (!clientId) {
      setCounts({ messages: 0, agreements: 0 });
      return;
    }
    const [msgRes, agrRes] = await Promise.all([
      PortalMessagesService.getUnreadCount(clientId),
      PortalAgreementsService.getActionRequiredCount(clientId)
    ]);
    setCounts({
      messages: msgRes.data || 0,
      agreements: agrRes.data || 0
    });
  }, []);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  useEffect(() => {
    if (client?.id) {
      loadCounts(client.id);
    }
  }, [client?.id, loadCounts]);

  const refreshCounts = useCallback(() => {
    if (client?.id) {
      loadCounts(client.id);
    }
  }, [client?.id, loadCounts]);

  const value = useMemo(() => ({
    client,
    clientId: client?.id || null,
    loading,
    error,
    refreshClient: loadClient,
    counts,
    refreshCounts,
    role,
    user
  }), [client, loading, error, loadClient, counts, refreshCounts, role, user]);

  return (
    <ClientPortalContext.Provider value={value}>
      {children}
    </ClientPortalContext.Provider>
  );
};

export const useClientPortal = () => {
  const ctx = useContext(ClientPortalContext);
  if (!ctx) {
    throw new Error('useClientPortal must be used within ClientPortalProvider');
  }
  return ctx;
};
